import { storedBillDiscountLine } from "@/modules/pos/lib/billDiscount";
import type { ReactNode } from "react";
import type {
  InvoiceChannel,
  PrintArtifactAction,
  RestaurantPrintLine,
  RestaurantPrintRenderModel,
} from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { translatorFor, type TranslateFn } from "@/lib/i18n/intl";
import type { PaperWidth } from "@/lib/printing";
import { billCustomerLines, billPlaceLabel, channelLabel } from "@/lib/invoice/place";
import { PreBillCheckPaperBody } from "@/modules/pos/components/PreBillCheck";
import { invoiceForCheck, invoiceForPlates } from "@/modules/pos/lib/preBillCheck";
import { moneyFormatter } from "@/shared/ui";
import {
  firstAddressLine,
  kitchenFirstName,
  riderCollect,
} from "@/lib/invoice/paperLines";
import {
  chitAdditionNumber,
  chitClock,
  groupChitLines,
  voidRemainder,
  type ChitLineFacts,
} from "@/lib/invoice/chitLines";
import { voidReasonLabelForCode } from "@/modules/pos/lib/voidReasons";
import { receiptFooterForLocale } from "@/modules/pos/lib/receiptFooter";
import { renderStandalonePrintArtifact } from "./renderDocument";
import {
  PaperFooter,
  PaperHead,
  PaperItems,
  PaperSumRow,
  PaperTotal,
  paperAmount,
  orderFact,
  paperStamp,
  peopleFacts,
  type PaperFact,
  type PaperItem,
} from "./paperLayout";

type PrintInvoice = NonNullable<RestaurantPrintRenderModel["invoice"]>;

/**
 * The render model's `channel` is a raw `string` off the wire; the label
 * helper wants the closed union. A channel this build has no word for prints
 * as itself rather than as a confident wrong word (the rule `channelLabel`'s
 * own header states).
 */
function channelOf(invoice: PrintInvoice): InvoiceChannel | null {
  return invoice.channel === "DINE_IN" ||
    invoice.channel === "TAKEAWAY" ||
    invoice.channel === "DELIVERY"
    ? invoice.channel
    : null;
}

/**
 * WHERE THE PAPER IS GOING (T9): the table names, else the channel's LABEL in
 * the paper's own language — this used to print the raw enum, so a delivery
 * receipt in the bag said `DELIVERY` in capitals over an Arabic total.
 */
function placeLabel(invoice: PrintInvoice, t: TranslateFn): string {
  if (invoice.tables.length > 0) return invoice.tables.join(" + ");
  const channel = channelOf(invoice);
  return channel ? channelLabel(channel, t) : invoice.channel;
}

/**
 * WHO THE BAG IS FOR, through the same helper the till panel and the browser
 * receipt read (`billCustomerLines`), so the printer cannot name a customer
 * differently from the screen the order was typed on. Empty on dine-in and on
 * a model from a server that sends no customer fields.
 */
function customerLines(invoice: PrintInvoice) {
  const channel = channelOf(invoice);
  if (!channel) return [];
  return billCustomerLines({
    channel,
    customerName: invoice.customerName ?? null,
    customerPhone: invoice.customerPhone ?? null,
    deliveryAddress: invoice.deliveryAddress ?? null,
  });
}

/**
 * THE OFF-PREMISE BAND (T19): the daily number LARGE, then channel · platform
 * · platform order no. — the three things a rider, a cook and a cashier all
 * match a bag by. A dine-in paper keeps its one-line "#42 · T4" header.
 */
function HeaderBand({ invoice, t, extra }: { invoice: PrintInvoice; t: TranslateFn; extra?: ReactNode }) {
  const channel = channelOf(invoice);
  if (channel === "DINE_IN" || channel === null) {
    return (
      <p style={{ textAlign: "center", fontWeight: 800 }}>
        #{invoice.dailySequence} · {placeLabel(invoice, t)}{extra}
      </p>
    );
  }
  const band = [
    channelLabel(channel, t),
    invoice.aggregatorName ?? null,
    invoice.platformOrderRef ?? null,
  ].filter((value): value is string => Boolean(value));
  return (
    <div data-print-band="" style={{ textAlign: "center", marginBottom: "1.5mm" }}>
      <div style={{ fontSize: "24px", fontWeight: 900, lineHeight: 1.1 }}>
        #{invoice.dailySequence}
      </div>
      {/* Each segment isolated (`<bdi>`), for the reason the browser chit gives:
          a Latin platform or reference inside an Arabic run otherwise reorders
          across the separators. */}
      <div style={{ fontWeight: 800, fontSize: "13px" }}>
        {band.map((segment, index) => (
          <span key={segment}>
            {index > 0 ? " · " : null}
            <bdi>{segment}</bdi>
          </span>
        ))}
        {extra}
      </div>
    </div>
  );
}

function customerLineLabel(key: "name" | "phone" | "address", t: TranslateFn): string {
  return key === "name"
    ? t((m) => m.pos.customerName)
    : key === "phone"
      ? t((m) => m.pos.customerPhone)
      : t((m) => m.pos.customerAddress);
}

type Named = { nameAr?: unknown; nameEn?: unknown; name?: unknown; quantity?: unknown };
type Swap = {
  removedNameAr?: unknown;
  addedNameAr?: unknown;
  removedName?: unknown;
  addedName?: unknown;
};

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (entry): entry is Record<string, unknown> =>
          Boolean(entry) && typeof entry === "object" && !Array.isArray(entry),
      )
    : [];
}

function localizedName(value: Named, locale: Locale): string | null {
  const ar = typeof value.nameAr === "string" ? value.nameAr : null;
  const en = typeof value.nameEn === "string" ? value.nameEn : null;
  const generic = typeof value.name === "string" ? value.name : null;
  return locale === "en" ? en ?? ar ?? generic : ar ?? en ?? generic;
}

/** A line's printed details in their three kinds (T10 groups on all three). */
function modifierParts(
  line: RestaurantPrintLine,
  locale: Locale,
): { combos: string[]; swaps: string[]; addons: string[] } {
  const addonNames = records(line.addons)
    .map((entry) => localizedName(entry, locale))
    .filter((value): value is string => Boolean(value));
  const combos = records(line.comboComponents)
    .map((entry) => {
      const name = localizedName(entry, locale);
      if (!name) return null;
      const quantity = typeof entry.quantity === "number" ? entry.quantity : 1;
      return quantity > 1 ? `${name} ×${quantity}` : name;
    })
    .filter((value): value is string => Boolean(value));
  const swaps = records(line.substitutions)
    .map((entry: Swap) => {
      const removed =
        typeof entry.removedNameAr === "string"
          ? entry.removedNameAr
          : typeof entry.removedName === "string"
            ? entry.removedName
            : null;
      const added =
        typeof entry.addedNameAr === "string"
          ? entry.addedNameAr
          : typeof entry.addedName === "string"
            ? entry.addedName
            : null;
      return removed && added ? `− ${removed} ⟵ + ${added}` : null;
    })
    .filter((value): value is string => Boolean(value));
  return { combos, swaps, addons: addonNames };
}

function modifiers(line: RestaurantPrintLine, locale: Locale): string[] {
  const parts = modifierParts(line, locale);
  return [...parts.combos, ...parts.swaps, ...parts.addons];
}

/**
 * THE PAPER'S MARKER WORDS (T14, D172) — always the catalogue's, never the
 * enum: an Arabic print computer printed `VOID` in capitals over an Arabic
 * chit. Symbol first; the same words mark the job and each line.
 */
function actionLabel(action: string, t: TranslateFn): string | null {
  switch (action) {
    case "VOID":
      return t((m) => m.printArtifact.void);
    case "CHANGE":
      return t((m) => m.printArtifact.change);
    case "REMAKE":
      return t((m) => m.printArtifact.remake);
    case "REROUTE":
      return t((m) => m.printArtifact.reroute);
    case "REPRINT":
      return t((m) => m.printArtifact.reprint);
    default:
      return null;
  }
}

/**
 * A paper says «نسخة مكررة» only when it IS an extra copy (T14): a paper
 * re-sent because the first never reached the kitchen is the first the cook
 * sees, and marking it a duplicate would get it binned. A reprint with no
 * recorded reason (a recovery successor) keeps the warning. An EXTRA_COPY is
 * marked whatever its action: a ticket whose first paper came from the
 * browser has no earlier job, so its extra copy is written as an ORIGINAL.
 * A `DELIVERY_UNKNOWN` reprint (D172 rule B — an earlier paper of the ticket
 * may be printing) is marked whatever the till answered.
 */
function marksDuplicate(job: Pick<RestaurantPrintRenderModel["job"], "action" | "requestedReason">): boolean {
  if (job.requestedReason === "EXTRA_COPY" || job.requestedReason === "DELIVERY_UNKNOWN") return true;
  return job.action === "REPRINT" && job.requestedReason !== "NOT_RECEIVED";
}

function Marker({
  job,
  t,
  kitchen = false,
}: {
  job: RestaurantPrintRenderModel["job"];
  t: TranslateFn;
  kitchen?: boolean;
}) {
  const duplicate = marksDuplicate(job);
  const action: PrintArtifactAction = duplicate ? "REPRINT" : job.action;
  if (action === "ORIGINAL") return null;
  if (action === "REPRINT" && !duplicate) return null;
  const label = actionLabel(action, t);
  if (!label) return null;
  // ≥ 15 px / 900; the cancellation REVERSED (white on black), so it cannot be
  // read as an ordinary boxed heading at arm's length.
  const reversed = action === "VOID";
  return (
    <div
      className="print-artifact-marker"
      data-print-marker={action}
      style={{
        fontSize: "17px",
        fontWeight: 900,
        ...(reversed ? { background: "#000", color: "#fff" } : {}),
      }}
    >
      {label}
      {kitchen && action === "REPRINT" ? (
        <small>{t((m) => m.printArtifact.reprintNote)}</small>
      ) : null}
    </div>
  );
}

function LineDetails({ line, locale }: { line: RestaurantPrintLine; locale: Locale }) {
  const details = modifiers(line, locale);
  return (
    <>
      {details.map((detail, index) => (
        <div key={`${line.id}:detail:${index}`} style={{ paddingInlineStart: "3mm", fontSize: "10px" }}>
          {detail}
        </div>
      ))}
      {line.note ? (
        <div style={{ paddingInlineStart: "3mm", fontSize: "10px", fontWeight: 700 }}>
          • {line.note}
        </div>
      ) : null}
    </>
  );
}

function variantNames(line: RestaurantPrintLine, locale: Locale): string[] {
  return (line.variantNames ?? [])
    .map((variant) => (locale === "en" && variant.nameEn ? variant.nameEn : variant.nameAr))
    .filter((name) => Boolean(name));
}

function chitFacts(line: RestaurantPrintLine, locale: Locale): ChitLineFacts {
  const parts = modifierParts(line, locale);
  return {
    dish: locale === "en" && line.nameEn ? line.nameEn : line.nameAr,
    variants: variantNames(line, locale),
    addons: parts.addons,
    swaps: parts.swaps,
    combo: parts.combos,
    note: line.note,
    // T10 — corrections print one per plate.
    groupable: line.action === "ITEM",
  };
}

function KitchenDocument({ model, locale, t }: { model: RestaurantPrintRenderModel; locale: Locale; t: TranslateFn }) {
  const station =
    locale === "en" && model.job.stationNameEn
      ? model.job.stationNameEn
      : model.job.stationNameAr;
  const invoice = model.invoice;
  // T9 — an off-premise chit names the CUSTOMER and the PLATFORM, so two
  // "Ahmed" bags on the pass do not swap; never a phone or an address, which
  // a cook has no use for and the server does not send on a chit.
  const offPremise = invoice ? channelOf(invoice) !== "DINE_IN" : false;
  const customer = invoice ? kitchenFirstName(invoice.customerName) : null;
  const kitchen = model.kitchen ?? null;
  const addition = chitAdditionNumber(kitchen?.round);
  // A1 — the fire time in the STORE's zone, beside the round.
  const head = [
    addition !== null ? t((m) => m.printArtifact.addition, { number: addition }) : null,
    kitchen ? chitClock(kitchen.firedAt, kitchen.timezone) : null,
  ].filter((value): value is string => Boolean(value));
  const groups = groupChitLines(model.lines, kitchen?.grouped ?? true, (line) => chitFacts(line, locale));
  return (
    <main className="receipt" data-print-artifact="KITCHEN_CHIT">
      <Marker job={model.job} t={t} kitchen />
      <h1 style={{ margin: 0, textAlign: "center", fontSize: "16px" }}>{station}</h1>
      {invoice ? <HeaderBand invoice={invoice} t={t} /> : null}
      {head.length > 0 ? (
        <p style={{ textAlign: "center", fontWeight: 800, fontSize: "13px", margin: "0 0 1mm" }} data-print-chit-head="">
          {head.map((segment, index) => (
            <span key={segment}>
              {index > 0 ? " · " : null}
              <bdi>{segment}</bdi>
            </span>
          ))}
        </p>
      ) : null}
      {/* T19 — a FIRST NAME only (the staff meal's name whole): a cook calls
          "Ahmed" across the pass, and a chit left lying there should carry
          no more of a customer. */}
      {invoice && offPremise && customer ? (
        <p style={{ textAlign: "center", fontWeight: 800, fontSize: "16px", margin: "0 0 1mm" }} data-print-customer="">
          {customer}
        </p>
      ) : null}
      <hr />
      {groups.map((group) => {
        const line = group.line;
        const variants = variantNames(line, locale);
        const lineAction = line.action === "ITEM" ? null : actionLabel(line.action, t);
        const remainder = line.action === "VOID" ? voidRemainder(line) : null;
        return (
          <section key={group.key} style={{ marginBottom: "2mm" }} data-print-line={line.action}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "2mm" }}>
              {/* T10 — the count FIRST and at least twice the dish's size. */}
              {group.count > 1 ? (
                <bdi dir="ltr" data-print-count="" style={{ fontSize: "36px", fontWeight: 900, lineHeight: 1 }}>
                  {`${group.count}×`}
                </bdi>
              ) : null}
              <span style={{ fontSize: "18px", fontWeight: 800 }}>
                {locale === "en" && line.nameEn ? line.nameEn : line.nameAr}
              </span>
            </div>
            {/* T10 — side and doneness on their own line under the name. */}
            {variants.length > 0 ? (
              <div style={{ fontSize: "14px", fontWeight: 800 }} data-print-variant="">
                {variants.join(" · ")}
              </div>
            ) : null}
            {lineAction ? (
              <div style={{ fontSize: "15px", fontWeight: 900 }} data-print-line-action="">
                {lineAction}
                {line.remakeSequence ? <bdi dir="ltr">{` #${line.remakeSequence}`}</bdi> : null}
              </div>
            ) : null}
            {remainder ? (
              <div style={{ fontSize: "14px", fontWeight: 900 }} data-print-void-remainder="">
                {t((m) => m.printArtifact.voidRemainder, {
                  voided: remainder.voided,
                  total: remainder.total,
                  remaining: remainder.remaining,
                })}
              </div>
            ) : null}
            {/* A1/B117 — why it came back, so the cook does not remake the
                same mistake; the note behind an OTHER beside it. */}
            {line.action === "REMAKE" && line.remakeReason ? (
              <div style={{ fontSize: "12px", fontWeight: 800 }} data-print-remake-reason="">
                {t((m) => m.kitchen.remakeReasonLine, {
                  reason: voidReasonLabelForCode(t, line.remakeReason, "remake"),
                })}
                {line.remakeReasonNote ? ` — ${line.remakeReasonNote}` : null}
              </div>
            ) : null}
            <LineDetails line={line} locale={locale} />
          </section>
        );
      })}
    </main>
  );
}

/** A charge row with a bare figure ("67.2"), as the reference prints them. */
function MoneyLine({ label, value }: { label: ReactNode; value: number; format?: (value: number) => string }) {
  return <PaperSumRow label={label} value={paperAmount(value)} />;
}

/**
 * One paper row per DISH, not per plate: the revision lists each plate on its
 * own, and "Latte 1 / Latte 1 / Latte 1" is three rows a guest has to add up.
 * Plates group when everything printed about them is the same — name, extras
 * and price — so a latte with oat milk stays its own row.
 */
function receiptItems(lines: RestaurantPrintLine[], locale: Locale): PaperItem[] {
  const groups = new Map<string, PaperItem>();
  for (const line of lines) {
    const name = locale === "en" && line.nameEn ? line.nameEn : line.nameAr;
    const details = [
      ...modifiers(line, locale).map((text) => ({ text })),
      ...(line.note ? [{ text: `• ${line.note}` }] : []),
    ];
    const total = line.lineTotal ?? line.unitChargedPrice ?? 0;
    const key = JSON.stringify([name, details.map((detail) => detail.text), total]);
    const group = groups.get(key);
    if (group) {
      group.quantity += 1;
      group.total += total;
    } else {
      groups.set(key, { key, name, details, quantity: 1, unitPrice: total, total });
    }
  }
  return [...groups.values()];
}

function ReceiptDocument({
  model,
  locale,
  t,
  logo = null,
}: {
  model: RestaurantPrintRenderModel;
  locale: Locale;
  t: TranslateFn;
  logo?: string | null;
}) {
  const format = (value: number) =>
    new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  const invoice = model.invoice;
  const paper = model.paper ?? null;
  const stampLocale = locale === "en" ? "en" : "ar";
  const customer = invoice ? customerLines(invoice) : [];
  const seated = invoice ? invoice.tables.length > 0 && channelOf(invoice) === "DINE_IN" : false;
  const facts: PaperFact[] = invoice
    ? [
        orderFact(t, invoice.dailySequence, seated ? invoice.tables.join(" + ") : null),
        {
          key: "check-in",
          label: t((m) => m.paper.checkIn),
          value: paperStamp(invoice.openedAt, paper?.timezone),
          ltr: true,
        },
        // Check out = when this paper is printed (owner, 2026-10-08).
        {
          key: "check-out",
          label: t((m) => m.paper.checkOut),
          value: paperStamp(new Date().toISOString(), paper?.timezone),
          ltr: true,
        },
        ...peopleFacts(t, invoice.captainName ?? null),
      ]
    : [];
  /*
   * T17 — WHAT THE RIDER COLLECTS, only where the server states what has been
   * paid (`paidTotal`): a model without it cannot tell a paid bag from an
   * unpaid one, and "TO COLLECT 150" on a paid bag is the worse error.
   */
  const rider =
    invoice && channelOf(invoice) === "DELIVERY" && invoice.paidTotal !== undefined && invoice.total !== undefined
      ? riderCollect({
          onPlatform: Boolean(invoice.aggregatorName),
          total: invoice.total,
          paidTotal: invoice.paidTotal,
        })
      : null;
  return (
    <main className="receipt" data-print-artifact="CUSTOMER_RECEIPT">
      <Marker job={model.job} t={t} />
      <PaperHead
        storeName={paper?.receiptHeader ?? null}
        logoUrl={logo}
        place={null}
        facts={facts}
      />
      {paper?.taxRegistrationNumber ? (
        <div style={{ textAlign: "center", fontSize: "9px" }}>
          {t((m) => m.invoices.taxRegistrationNumber)}: <bdi dir="ltr">{paper.taxRegistrationNumber}</bdi>
        </div>
      ) : null}
      {invoice && !seated ? <HeaderBand invoice={invoice} t={t} /> : null}
      {/* T9 — who the bag is for and which platform brought it, above the
          lines: the customer reads the receipt for their own phone and
          address, and the shop reads the platform to know whose statement the
          money will be on. Only the lines that were filled in. */}
      {invoice && (customer.length > 0 || invoice.aggregatorName) ? (
        <div style={{ marginBottom: "2mm", fontSize: "11px" }} data-print-customer="">
          {customer.map((line) => (
            <div key={line.key}>
              <strong>{customerLineLabel(line.key, t)}: </strong>
              <bdi dir={line.key === "phone" ? "ltr" : "auto"}>{line.value}</bdi>
            </div>
          ))}
          {invoice.aggregatorName ? (
            <div>
              <strong>{t((m) => m.pos.offPremiseAggregatorLabel)}: </strong>
              <bdi>{invoice.aggregatorName}</bdi>
            </div>
          ) : null}
        </div>
      ) : null}
      <PaperItems items={receiptItems(model.lines, locale)} t={t} />
      {/* Only when there is one (owner, 2026-10-08). */}
      {invoice?.discountTotal ? (
        <MoneyLine label={t((m) => m.paper.discount)} value={-invoice.discountTotal} />
      ) : null}
      {/* D146 — the discount at the bill, its own line before the charges
          computed on what it left, so the paper's rungs add up to the total. */}
      {invoice?.billDiscountAmount && invoice.subtotal !== undefined ? (
        <MoneyLine
          label={t((m) => m.billDiscount.lineLabel)}
          value={-storedBillDiscountLine({
            subtotal: invoice.subtotal,
            billDiscount: {
              kind: "AMOUNT",
              value: invoice.billDiscountAmount,
              amount: invoice.billDiscountAmount,
              capPct: 100,
              reasonCode: "",
              reasonNote: null,
              bySellerId: null,
              at: "",
            },
            serviceChargePercent: invoice.serviceChargePercent ?? null,
            vatPercent: invoice.vatPercent ?? null,
            vatOnServiceCharge: invoice.vatOnServiceCharge ?? true,
            pricesIncludeTax: invoice.pricesIncludeTax ?? false,
          })}
          format={format}
        />
      ) : null}
      {invoice?.serviceChargeTotal ? (
        <MoneyLine
          label={
            invoice.serviceChargePercent != null
              ? t((m) => (invoice.pricesIncludeTax ? m.invoices.serviceChargeIncludedWithRate : m.invoices.serviceChargeWithRate), {
                  percent: String(Number(invoice.serviceChargePercent)),
                })
              : t((m) => m.invoices.serviceCharge)
          }
          value={invoice.serviceChargeTotal}
          format={format}
        />
      ) : null}
      {invoice?.vatTotal ? (
        <MoneyLine
          /* "VAT 14%" — the rate, not its base (owner, 2026-10-10: the
             "on 660.80" read badly and wrapped on 80 mm Arabic paper). */
          label={
            invoice.vatPercent != null
              ? t((m) => (invoice.pricesIncludeTax ? m.invoices.vatIncludedWithRate : m.invoices.vatWithRate), {
                  percent: String(Number(invoice.vatPercent)),
                })
              : t((m) => m.invoices.vat)
          }
          value={invoice.vatTotal}
          format={format}
        />
      ) : null}
      {invoice?.deliveryFee ? <MoneyLine label={t((m) => m.invoices.deliveryFee)} value={invoice.deliveryFee} format={format} /> : null}
      {/* T9 — the fee's OWN VAT, so the lines sum to the total: `vatTotal` is
          food VAT alone, and a receipt whose rungs do not add up is the first
          thing an accountant in the bag's household notices. */}
      {invoice?.deliveryFeeVat ? (
        <div style={{ paddingInlineStart: "3mm", fontSize: "10px" }}>
          <MoneyLine label={t((m) => m.invoices.deliveryFeeVat)} value={invoice.deliveryFeeVat} format={format} />
        </div>
      ) : null}
      {invoice?.total !== undefined ? (
        <PaperTotal
          label={t((m) => m.paper.total)}
          value={moneyFormatter(stampLocale)(invoice.total)}
          status={null}
        />
      ) : null}
      {/* T17 — THE RIDER BLOCK, at the foot (paper persona, 2026-10-07) and on a
          DELIVERY only: the amount to collect, large and LTR, with the
          phone, the first address line and the bill number — what is read in
          a doorway. On a platform's order or a bill already paid there is no
          headline at all (founder, 2026-10-08), only those facts, so a rider
          never collects twice. */}
      {invoice && rider ? (
        <div
          data-print-rider=""
          style={{ border: "2px solid #000", padding: "1.5mm", margin: "2mm 0 0", textAlign: "center" }}
        >
          {rider.kind === "collect" ? (
            <>
              <div style={{ fontSize: "11px", fontWeight: 800 }}>{t((m) => m.pos.receiptToCollect)}</div>
              <div dir="ltr" style={{ fontSize: "22px", fontWeight: 900, lineHeight: 1.1 }} data-print-rider-amount="">
                {format(rider.amount)}
              </div>
            </>
          ) : null}
          {/* Nothing to collect: no headline (founder, 2026-10-08), only the
              facts the courier still needs. */}
          <div style={{ fontSize: "11px", marginTop: rider.kind === "collect" ? "1mm" : 0 }} data-print-rider-facts="">
            {[
              invoice.customerPhone ? `\u2066${invoice.customerPhone}\u2069` : null,
              firstAddressLine(invoice.deliveryAddress),
              `\u2066#${invoice.dailySequence}\u2069`,
            ]
              .filter((value): value is string => Boolean(value))
              .join(" · ")}
          </div>
        </div>
      ) : null}
      {/* The paid copy says so (owner, 2026-10-11), with when it was paid. */}
      {invoice?.status === "SETTLED" ? (
        <div style={{ marginTop: "1.5mm" }} data-print-paid="">
          <PaperSumRow label={t((m) => m.paper.status)} value={<strong>{t((m) => m.paper.statusPaid)}</strong>} />
          {invoice.settledAt ? (
            <PaperSumRow
              label={t((m) => m.pos.receiptSettledAtLabel)}
              value={paperStamp(invoice.settledAt, paper?.timezone)}
            />
          ) : null}
        </div>
      ) : null}
      <PaperFooter
        text={receiptFooterForLocale(paper, stampLocale)}
        poweredBy={t((m) => m.paper.poweredBy)}
      />
    </main>
  );
}

/**
 * The pre-bill check from its job's frozen snapshot (docs §11), through the
 * till's own paper and its own plate selection, so the receipt printer prints
 * the check the till recorded — whichever device pressed the button.
 */
function CheckDocument({
  model,
  locale,
  t,
  logo,
}: {
  model: RestaurantPrintRenderModel;
  locale: Locale;
  t: TranslateFn;
  logo: string | null;
}) {
  const snapshot = model.checkSnapshot;
  if (!snapshot) {
    throw new Error("A pre-bill check job carries no check snapshot.");
  }
  const invoice = snapshot.itemIds
    ? invoiceForPlates(snapshot.invoice, snapshot.itemIds)
    : invoiceForCheck(snapshot.invoice);
  // Table names in the bill's own table order, as the till labels the place.
  const names = new Map(snapshot.invoice.tableIds.map((id, index) => [id, snapshot.tableNames[index] ?? id]));
  const paperLocale = locale === "en" ? "en" : "ar";
  return (
    <PreBillCheckPaperBody
      invoice={invoice}
      splitGuests={snapshot.splitGuests}
      receiptHeader={snapshot.receiptHeader}
      timezone={snapshot.timezone}
      tableName={billPlaceLabel(snapshot.invoice, (id) => names.get(id) ?? id, t)}
      captainName={snapshot.captainName ?? null}
      logoUrl={logo}
      footer={receiptFooterForLocale(
        { receiptFooter: snapshot.receiptFooter ?? null, receiptFooterEn: snapshot.receiptFooterEn ?? null },
        paperLocale,
      )}
      t={t}
      locale={paperLocale}
      direction={paperLocale === "ar" ? "rtl" : "ltr"}
      format={moneyFormatter(paperLocale)}
    />
  );
}

export async function renderCoordinatorArtifact(input: {
  model: RestaurantPrintRenderModel;
  locale: Locale;
  t: TranslateFn;
  /** The store's logo as an embedded image (a data URL), for the customer papers' head. */
  logoDataUrl?: string | null;
}) {
  const width: PaperWidth = input.model.job.paperWidth === "MM58" ? "58mm" : "80mm";
  const kind = input.model.job.kind;
  // THE CUSTOMER PAPERS PRINT IN THE STORE'S RECEIPT LANGUAGE (owner,
  // 2026-10-08), frozen on the check's snapshot and sent with the receipt —
  // not in the language of whichever screen the print computer is showing.
  // Kitchen tickets keep the screen's. A model from an older server carries
  // no language and prints as before.
  const paperLanguage =
    kind === "PRE_BILL_CHECK"
      ? input.model.checkSnapshot?.language
      : kind === "CUSTOMER_RECEIPT"
        ? input.model.paper?.receiptLanguage
        : undefined;
  const locale: Locale = paperLanguage ?? input.locale;
  const t = paperLanguage && paperLanguage !== input.locale ? translatorFor(paperLanguage) : input.t;
  const logo = input.logoDataUrl ?? null;
  const content =
    kind === "PRE_BILL_CHECK" ? (
      <CheckDocument model={input.model} locale={locale} t={t} logo={logo} />
    ) : kind === "KITCHEN_CHIT" ? (
      <KitchenDocument model={input.model} locale={input.locale} t={input.t} />
    ) : kind === "CUSTOMER_RECEIPT" ? (
      <ReceiptDocument model={input.model} locale={locale} t={t} logo={logo} />
    ) : (
      <main className="receipt"><p style={{ textAlign: "center", fontWeight: 800 }}>TEST</p></main>
    );
  return renderStandalonePrintArtifact({
    kind: kind === "KITCHEN_CHIT" || kind === "PRE_BILL_CHECK" ? kind : "CUSTOMER_RECEIPT",
    action: input.model.job.action,
    locale,
    paperWidth: width,
    title: input.model.job.printerLabel ?? kind,
    content,
  });
}
